import {
  Box,
  HStack,
  Icon,
  IconButton,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from '@wegroup/design-system';
import React, { forwardRef, RefObject, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  name: string;
  title: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur: () => void;
  onClose: () => void;
  isPinned: boolean;
  onPinClick: () => void;
  notesRef: RefObject<HTMLTextAreaElement>;
}

const NoteInput = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      name,
      title,
      value,
      onChange,
      onBlur,
      onClose,
      isPinned,
      onPinClick,
      notesRef,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    useEffect(() => {
      if (notesRef.current)
        notesRef.current.selectionStart = value?.length || 0;
    }, []);

    return (
      <Box>
        <HStack bg="gray.ultraLight" p="4" spacing="4" justify="space-between">
          <VStack spacing="1" align="stretch">
            <Text textStyle="smallBody" color="text.gray">
              {t('ADDITIONAL-REMARKS')}
            </Text>
            <Text textStyle="largeBodyBold">{title}</Text>
          </VStack>

          <HStack spacing="2">
            <Tooltip
              label={t('FLOWS_INPUT-COMPONENT_NOTES_PIN-NOTES')}
              zIndex="modal"
            >
              <IconButton
                size="xs"
                variant={isPinned ? 'secondary' : 'grayedGhost'}
                icon={<Icon name="GeneralOthersPin" boxSize={5} />}
                aria-label="Pin notes"
                onClick={onPinClick}
              />
            </Tooltip>
            <IconButton
              size="xs"
              p="0"
              variant="grayedGhost"
              icon={<Icon name="GeneralActionsMinusOrRemove" boxSize={5} />}
              border="2px"
              borderColor="gray.300"
              borderRadius="sm"
              aria-label="Close notes"
              onClick={onClose}
            />
          </HStack>
        </HStack>
        <Textarea
          ref={ref}
          p="3"
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          resize="none"
          rows={12}
          borderRadius="none"
          border="none"
          whiteSpace="pre-wrap"
          placeholder={t('FLOWS_INPUT-COMPONENT_NOTES_TYPE-NOTES-HERE') + '...'}
          autoFocus
        />
      </Box>
    );
  },
);

export default NoteInput;
